import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button,
  CircularProgress, TextField, Grid, Switch, FormControlLabel, InputAdornment, Select, MenuItem,
  FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Tooltip, IconButton
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import axios from 'axios';
import { API, getAuthHeader, fmtDate, numField } from './customerApp/shared';

const EVENT_LABEL = {
  signup: 'Friend joined',
  first_service: 'First service',
  first_subscription: 'First membership',
  adjustment: 'Owner adjustment',
  redemption: 'Redeemed'
};
const EVENT_COLOR = { signup: 'info', first_service: 'success', first_subscription: 'secondary', adjustment: 'default', redemption: 'warning' };
const EventChip = ({ event }) => <Chip label={EVENT_LABEL[event] || event} size="small" color={EVENT_COLOR[event] || 'default'} variant={event === 'adjustment' ? 'outlined' : 'filled'} />;
const points = (n) => `${n > 0 ? '+' : ''}${Number(n || 0).toLocaleString()}`;

const copy = (text, notify) => {
  navigator.clipboard?.writeText(text).then(() => notify('Copied', 'success')).catch(() => notify(text, 'info'));
};

function ProgramCard({ program, setProgram, onSave, saving }) {
  const set = (field, value) => setProgram((p) => ({ ...p, [field]: value }));
  const percents = program.generationPercents || [];
  const setPercent = (i, value) => set('generationPercents', percents.map((p, j) => (j === i ? value : p)));
  const addGeneration = () => set('generationPercents', [...percents, Math.max(1, Math.floor((Number(percents[percents.length - 1]) || 50) / 2))]);
  const removeGeneration = (i) => set('generationPercents', percents.filter((_, j) => j !== i));
  const example = (base) => percents.map((p) => Math.floor(Number(base || 0) * (Number(p) || 0) / 100));

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">Program</Typography>
        <Button variant="contained" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save program'}</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every customer with an account gets a share code and link (Account → Refer friends in the app). When someone joins with it, the
        customer earns Knights three times: when the friend creates an account, when the friend pays for their first service, and when the
        friend pays for their first membership. The friend&apos;s own referrals pay too, at the diminishing rates below, as many generations
        deep as you list. Each award happens once per referred customer.
      </Typography>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Grid item xs={12} sm={4}>
          <FormControlLabel control={<Switch checked={program.enabled === true} onChange={(e) => set('enabled', e.target.checked)} />} label={program.enabled === true ? 'Knights program is ON' : 'Knights program is OFF'} />
          <Typography variant="caption" color="text.secondary" display="block">
            Off (the default until you launch): the app shows no Refer friends row or Knights wording, no share codes are issued, and nothing is attributed or awarded. Anyone who already holds Knights keeps them and can still see them.
          </Typography>
        </Grid>
        <Grid item xs={4} sm={8 / 3}>
          <TextField {...numField({ label: 'Friend joins' })} value={program.pointsSignup ?? ''} onChange={(e) => set('pointsSignup', e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">Knights</InputAdornment> }} inputProps={{ min: 0 }} />
        </Grid>
        <Grid item xs={4} sm={8 / 3}>
          <TextField {...numField({ label: 'First service paid' })} value={program.pointsFirstService ?? ''} onChange={(e) => set('pointsFirstService', e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">Knights</InputAdornment> }} inputProps={{ min: 0 }} />
        </Grid>
        <Grid item xs={4} sm={8 / 3}>
          <TextField {...numField({ label: 'First membership paid' })} value={program.pointsFirstSubscription ?? ''} onChange={(e) => set('pointsFirstSubscription', e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">Knights</InputAdornment> }} inputProps={{ min: 0 }} />
        </Grid>
      </Grid>

      <Typography variant="subtitle2" sx={{ mt: 2 }}>Generations</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Percent of the amounts above paid to each generation up the chain. Generation 1 is the person who shared the code; generation 2 is
        whoever referred them; and so on. Remove a row to stop the chain there.
      </Typography>
      <Table size="small" sx={{ maxWidth: 720 }}>
        <TableHead>
          <TableRow><TableCell>Generation</TableCell><TableCell>Rate</TableCell><TableCell>Friend joins</TableCell><TableCell>First service</TableCell><TableCell>First membership</TableCell><TableCell /></TableRow>
        </TableHead>
        <TableBody>
          {percents.map((p, i) => (
            <TableRow key={i}>
              <TableCell>{i + 1}{i === 0 ? ' (direct)' : ''}</TableCell>
              <TableCell sx={{ width: 140 }}>
                <TextField {...numField({ label: '' })} value={p ?? ''} onChange={(e) => setPercent(i, e.target.value)}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} inputProps={{ min: 0, max: 100 }} />
              </TableCell>
              <TableCell>{example(program.pointsSignup)[i]}</TableCell>
              <TableCell>{example(program.pointsFirstService)[i]}</TableCell>
              <TableCell>{example(program.pointsFirstSubscription)[i]}</TableCell>
              <TableCell align="right">
                {percents.length > 1 && <IconButton size="small" onClick={() => removeGeneration(i)} aria-label="Remove generation"><DeleteOutlineIcon fontSize="small" /></IconButton>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {percents.length < 10 && <Button size="small" sx={{ mt: 1 }} onClick={addGeneration}>Add generation {percents.length + 1}</Button>}

      <TextField
        fullWidth multiline minRows={3} size="small" sx={{ mt: 2 }} label="How Knights can be used (shown in the app)"
        value={program.terms || ''} onChange={(e) => set('terms', e.target.value)}
        helperText="Tell customers what Knights are worth and how to redeem them, e.g. “100 Knights = $1 off any service. Call or message support to redeem.” Redemptions are recorded by hand below."
      />
    </Paper>
  );
}

function AdjustDialog({ customer, onClose, notify, onChanged }) {
  const [pts, setPts] = useState('');
  const [note, setNote] = useState('');
  const [redemption, setRedemption] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPts(''); setNote(''); setRedemption(true); }, [customer]);

  const submit = async () => {
    const n = Math.trunc(Number(pts));
    if (!n) { notify('Enter the number of Knights', 'warning'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/knights-admin/customers/${customer.id}/adjust`, { points: redemption ? -Math.abs(n) : n, note, redemption }, { headers: getAuthHeader() });
      notify(redemption ? 'Redemption recorded' : 'Knights adjusted', 'success');
      onChanged();
      onClose();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!customer} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{customer?.name}: {redemption ? 'record a redemption' : 'adjust Knights'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Balance: <b>{Number(customer?.balance || 0).toLocaleString()}</b> Knights</Typography>
        <FormControlLabel control={<Switch checked={redemption} onChange={(e) => setRedemption(e.target.checked)} />} label={redemption ? 'Redemption (takes Knights off their balance)' : 'Adjustment (+ adds, − removes)'} />
        <TextField {...numField({ label: redemption ? 'Knights redeemed' : 'Knights (+/−)' })} fullWidth sx={{ mt: 2 }} value={pts} onChange={(e) => setPts(e.target.value)} autoFocus />
        <TextField fullWidth size="small" sx={{ mt: 2 }} label="Note (the customer sees this)" value={note} onChange={(e) => setNote(e.target.value)} placeholder={redemption ? 'e.g. $5 off jump start on 10/14' : 'e.g. Goodwill for the delay'} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy}>{busy ? 'Saving...' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function LedgerTable({ notify, refreshKey }) {
  const [event, setEvent] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/knights-admin/ledger${event ? `?event=${event}` : ''}`, { headers: getAuthHeader() });
      setRows(data.ledger || []);
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to load the ledger', 'error');
    } finally {
      setLoading(false);
    }
  }, [event, notify]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Ledger</Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Event</InputLabel>
          <Select label="Event" value={event} onChange={(e) => setEvent(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(EVENT_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow><TableCell>When</TableCell><TableCell>Customer</TableCell><TableCell>Event</TableCell><TableCell>Because of</TableCell><TableCell>Gen</TableCell><TableCell align="right">Knights</TableCell><TableCell>Note</TableCell></TableRow>
        </TableHead>
        <TableBody>
          {loading && rows.length === 0 && <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={20} /></TableCell></TableRow>}
          {!loading && rows.length === 0 && <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">Nothing yet</Typography></TableCell></TableRow>}
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.createdAt)}</TableCell>
              <TableCell>{r.customerName || r.customerEmail || '—'}</TableCell>
              <TableCell><EventChip event={r.event} /></TableCell>
              <TableCell>{r.sourceName || r.sourceEmail || '—'}</TableCell>
              <TableCell>{r.generation || '—'}</TableCell>
              <TableCell align="right" sx={{ color: r.points < 0 ? 'error.main' : 'success.main', fontWeight: 600 }}>{points(r.points)}</TableCell>
              <TableCell>{r.note || ''}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

function Knights() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [program, setProgram] = useState(null);
  const [totals, setTotals] = useState(null);
  const [top, setTop] = useState([]);
  const [adjusting, setAdjusting] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [snack, setSnack] = useState(null);

  const notify = useCallback((message, severity = 'info') => setSnack({ message, severity }), []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/api/knights-admin`, { headers: getAuthHeader() });
      setTotals(data.totals);
      setTop(data.top || []);
      setProgram((p) => p || data.program);
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to load the Knights program', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const saveProgram = async () => {
    try {
      setSaving(true);
      const { data } = await axios.put(`${API}/api/customer-admin/config`, {
        knights: {
          enabled: program.enabled === true,
          pointsSignup: Number(program.pointsSignup),
          pointsFirstService: Number(program.pointsFirstService),
          pointsFirstSubscription: Number(program.pointsFirstSubscription),
          generationPercents: (program.generationPercents || []).map((p) => Number(p)),
          terms: program.terms || ''
        }
      }, { headers: getAuthHeader() });
      setProgram(data.config.knights);
      notify('Program saved', 'success');
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to save program', 'error');
    } finally {
      setSaving(false);
    }
  };

  const changed = () => { load(); setRefreshKey((k) => k + 1); };

  if (loading && !program) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  const byEvent = totals?.byEvent || {};
  return (
    <Box>
      <Box sx={{ mb: 2, mt: 2 }}>
        <Typography variant="h5">Customer referrals (Knights)</Typography>
        <Typography variant="body2" color="text.secondary">Customers earn Knights for bringing in friends who join, buy a service or start a membership — and for their friends&apos; referrals, at a diminishing rate.</Typography>
      </Box>

      {program && <ProgramCard program={program} setProgram={setProgram} onSave={saveProgram} saving={saving} />}

      {totals && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
          <Chip label={`${totals.customersWithCode} customers with a code`} />
          <Chip label={`${totals.referredCustomers} referred customers`} color="info" variant="outlined" />
          <Chip label={`${Number(totals.issued).toLocaleString()} Knights issued`} color="success" variant="outlined" />
          <Chip label={`${Number(totals.redeemed).toLocaleString()} redeemed`} color="warning" variant="outlined" />
          <Chip label={`${Number(totals.outstanding).toLocaleString()} outstanding`} />
          {['signup', 'first_service', 'first_subscription'].map((e) => byEvent[e] && (
            <Tooltip key={e} title={`${byEvent[e].count} award${byEvent[e].count === 1 ? '' : 's'}`}><Chip size="small" label={`${EVENT_LABEL[e]}: ${Number(byEvent[e].points).toLocaleString()}`} variant="outlined" /></Tooltip>
          ))}
        </Box>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Top referrers</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell><TableCell>Code</TableCell><TableCell>Direct referrals</TableCell>
              <TableCell align="right">Earned</TableCell><TableCell align="right">Redeemed</TableCell><TableCell align="right">Balance</TableCell><TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {top.length === 0 && <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">Nobody has earned Knights yet</Typography></TableCell></TableRow>}
            {top.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>{c.name}<Typography variant="caption" display="block" color="text.secondary">{c.email}</Typography></TableCell>
                <TableCell>{c.code || '—'}{c.link && <Button size="small" sx={{ ml: 1 }} onClick={() => copy(c.link, notify)}>Copy link</Button>}</TableCell>
                <TableCell>{c.directReferrals}</TableCell>
                <TableCell align="right">{Number(c.earned).toLocaleString()}</TableCell>
                <TableCell align="right">{Number(c.redeemed).toLocaleString()}</TableCell>
                <TableCell align="right"><b>{Number(c.balance).toLocaleString()}</b></TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><Button size="small" onClick={() => setAdjusting(c)}>Redeem / adjust</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Customers who have not earned anything yet are not listed; their code and balance show under Customers &amp; claims → open the customer.
        </Typography>
      </Paper>

      <LedgerTable notify={notify} refreshKey={refreshKey} />

      <AdjustDialog customer={adjusting} onClose={() => setAdjusting(null)} notify={notify} onChanged={changed} />

      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">{snack.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

export default Knights;
